import { SelectProducts } from "@components/auto-completes/products";
import {
  SHAutocomplete,
  SHBooleanField,
  SHBox,
  SHButton,
  SHContainer,
  SHDivider,
  SHIconLoading,
  SHRadio,
  SHRichTextEditor,
  SHStack,
  SHTextField,
  SHTypography,
} from "@components/design-systems";
import UnsavedDialog from "@components/dialogs/unsaved";
import { StatusBadge } from "@components/status-badge";
import { CheckSVG, CloseSVG } from "@components/svgs";
import { APIExtRoutes, PageRoutes } from "@constants";
import { DateTimeFormat } from "@constants/format";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIsNew } from "@hooks/useIsNew";
import { useProduct } from "@hooks/useProduct";
import { useRateCard } from "@hooks/useRateCard";
import { useSupplier } from "@hooks/useSupplier";
import { useUserPermissions } from "@hooks/userUserPermission";
import { FieldGroup } from "@layouts/form/field-group";
import { TopBar } from "@layouts/top-bar";
import { ExistingFeeScheduleDTO } from "@models/product/entities/product";
import {
  CreateRateCardDTO,
  RateCardDTO,
  RateCardStatus,
  RateCardUniqueNameLevel,
  UpdateRateCardDTO,
} from "@models/rate-card/entities/rateCard";
import { useTheme } from "@mui/material";
import { FREditFeesTable } from "@pages/suppliers/_id/_products/_id/edit/components/fees-and-rates/components/tables";
import { RootState } from "@redux/store";
import { checkRateCardUniqueName } from "@services/rate-card/rateCardService";
import { format } from "date-fns";
import { isEmpty, isNil } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import {
  buildRateCate,
  convertDataToSubmit,
  generateRateCardFormTitle,
  rateCardValidators,
} from "./util";
import { SpecialSectionNames } from "@pages/suppliers/constants";
import { ConfigurationFeeSectionDTO } from "@models/product/entities/subProduct";
import { AnalysisOverwriteFeeScheduleDialog } from "@pages/platform-analysis-v2/_rate-cards/_id/components/dialogs/overwrite-fee-schedule";
import { AnalysisRateCardSkeleton } from "@pages/platform-analysis-v2/_rate-cards/_id/components/skeletons/rate-card";

// TODO: This entire component needs to be refactored into smaller components, Enhancement ticket: SD-2329
export const RateCardManagePage = () => {
  const isNew = useIsNew();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const { isSupplierBdmSale, isAdviserGroup } = useUserPermissions();
  const { rateCardId } = useParams<{ rateCardId: string }>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { rateCardUI, rateCard } = useSelector(
    (state: RootState) => state.rateCard,
  );
  const { loadExistingFeeSchedule, loadSubProductSelection, loadSubProduct } =
    useProduct();
  const {
    loadRateCard,
    loadRateCardLOV,
    createRateCard,
    updateRateCard,
    updateRateCardStatus,
  } = useRateCard();
  const { loadSupplier, refreshSupplier } = useSupplier();

  const formControls = useForm({
    mode: "onChange",
    defaultValues: {
      ...new RateCardDTO(),
    },
    resolver: yupResolver(rateCardValidators),
  });

  const {
    watch,
    reset,
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isSubmitting, dirtyFields },
  } = formControls;

  const isDirty = !isEmpty(dirtyFields);

  const [isOverriding, setIsOverriding] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState<
    | {
        feeScheduleId: string;
        isSubProduct?: boolean;
      }
    | undefined
  >(undefined);
  const [isChangingStatus, setIsChangingStatus] =
    useState<RateCardStatus | null>();
  const [feeSchedules, setFeeSchedules] = useState<ExistingFeeScheduleDTO[]>();
  const [selectedFeeSchedule, setSelectedFeeSchedule] =
    useState<ExistingFeeScheduleDTO>();
  const [subProducts, setSubProducts] = useState<ExistingFeeScheduleDTO[]>([]);
  const [selectedSubProduct, setSelectedSubProduct] =
    useState<ExistingFeeScheduleDTO | null>(null);

  const [verifyRateCardName, setVerifyRateCardName] = useState<
    "error" | "success" | "checking" | "none"
  >(isNew ? "none" : "success");
  const [isValidName, setIsValidName] = useState<boolean>(true);

  const timer = useRef<NodeJS.Timeout | null>(null);

  const productId = watch("productId");
  const currentStatus = watch("status");
  const { supplierData } = useSelector((state: RootState) => state.supplier);

  const shareLabelForSaleUser = useMemo(
    () => isSupplierBdmSale,
    [isSupplierBdmSale],
  );

  const isOwner = useMemo(() => {
    return rateCard?.ownerId === user?.auth0Id || isNew;
  }, [isNew, user?.auth0Id, rateCard?.ownerId]);

  const isDirtyRateCardData =
    !isNil(dirtyFields?.standardFeeSections) ||
    !isNil(dirtyFields?.otherFeeSections);

  const [verifyRateCardNameErrorMessage, setVerifyRateCardNameErrorMessage] =
    useState("");

  const sharedWithLinkedPractices =
    watch("isSharedWithLinkedPractices") ?? false;

  const loadRateCardDetails = async () => {
    let rateCard: RateCardDTO | undefined = undefined;

    if (isNew) {
      rateCard = await loadRateCardLOV();
    } else {
      if (!rateCardId) return;
      rateCard = await loadRateCard(rateCardId);
    }

    if (rateCard)
      reset({
        ...rateCard,
        isAvailableForAnalysis: isNew ? true : rateCard?.isAvailableForAnalysis,
        isShared: isNew ? true : rateCard?.isShared,
      });
  };

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
  };

  const handleRateCardNameChecking = async () => {
    const name = getValues("name");
    clearTimer();
    setIsValidName(false);
    if (
      !name ||
      name.trim().length === 0 ||
      (isAdviserGroup && !user?.userMetadata?.adviser_firm_id) ||
      (!isAdviserGroup && !user?.userMetadata?.supplier_id)
    ) {
      setVerifyRateCardName("none");
      return;
    }
    if (rateCard?.name === name && !isDirty) {
      setVerifyRateCardName("success");
      setIsValidName(true);
      return;
    }
    setVerifyRateCardName("checking");
    timer.current = setTimeout(async () => {
      const firmId = isAdviserGroup
        ? user?.userMetadata?.adviser_firm_id
        : user?.userMetadata?.supplier_id;

      const uniqueNameLevel = sharedWithLinkedPractices
        ? !isEmpty(productId)
          ? RateCardUniqueNameLevel.Platform
          : RateCardUniqueNameLevel.Firm
        : RateCardUniqueNameLevel.User;

      const { isSuccess, message } = await checkRateCardUniqueName(
        firmId ?? "",
        name,
        uniqueNameLevel,
        productId ?? "",
        rateCardId,
      );
      if (isSuccess) {
        setVerifyRateCardName("success");
        setIsValidName(true);
      } else {
        setVerifyRateCardName("error");
        setVerifyRateCardNameErrorMessage(message || "");
      }
    }, 300);
  };

  const createFeeTableElement = (
    section: ConfigurationFeeSectionDTO,
    sectionIndex: number,
    isOtherFee = false,
  ) => (
    <FREditFeesTable
      key={section.id}
      section={section}
      sectionIndex={sectionIndex}
      isDisabled={isSubmitting}
      isOtherFee={isOtherFee}
      isRateCard
      isEditSchedule={() => setSelectedFeeSchedule(undefined)}
    />
  );

  const renderStandardSections = () => {
    const standardSections = watch("standardFeeSections") ?? [];

    return standardSections
      .filter(
        (section) =>
          !SpecialSectionNames.SectionNamesToHide.includes(section.name),
      )
      .map((section, index) => createFeeTableElement(section, index));
  };

  const renderOtherSections = () => {
    const otherSections = watch("otherFeeSections") ?? [];
    const standardSections = watch("standardFeeSections") ?? [];

    const specialStandardSection = standardSections.find((section) =>
      SpecialSectionNames.SectionNamesToHide.includes(section.name),
    );

    return otherSections.flatMap((section, index) => {
      const elements = [createFeeTableElement(section, index, true)];

      if (
        section.name === SpecialSectionNames.FamilyFeeAggregation &&
        specialStandardSection
      ) {
        const specialSectionIndex = standardSections.findIndex(
          (s) => s.id === specialStandardSection.id,
        );
        elements.push(
          createFeeTableElement(specialStandardSection, specialSectionIndex),
        );
      }

      return elements;
    });
  };

  const renderVerifyRateCardName = () => {
    switch (verifyRateCardName) {
      case "success":
        return <CheckSVG width={18} height={18} />;
      case "error":
        return (
          <SHBox px={"4px"} py={"1px"}>
            <CloseSVG width={10} height={10} />
          </SHBox>
        );
      case "checking":
        return <SHIconLoading size={16} />;
      default:
        return null;
    }
  };

  const loadSubProducts = async () => {
    if (!productId) return;
    const response = await loadSubProductSelection(productId);
    if (response) {
      setSubProducts(response);
      const formDataSelectedSubProductId = getValues("subProductId");
      if (!isNew && formDataSelectedSubProductId) {
        const selectedSubProduct = response.find(
          (item) => item.id === formDataSelectedSubProductId,
        );
        setSelectedSubProduct(selectedSubProduct ?? null);
      } else {
        setSelectedSubProduct(null);
      }
    }
  };

  const loadFeeSchedules = async () => {
    if (!productId) return;
    const response = await loadExistingFeeSchedule(productId);
    if (response) {
      const feeSchedules = response.filter((item) => item?.id !== rateCardId);
      setFeeSchedules(feeSchedules);
    }
  };

  const handleSubmitRateCard = async (rateCard: RateCardDTO) => {
    if (isNew) {
      await handleOnCreateRateCard(rateCard);
    } else {
      await handleOnUpdateRateCard(rateCard);
    }
  };

  const handleOnCreateRateCard = async (rateCard: RateCardDTO) => {
    const res = await createRateCard(
      convertDataToSubmit(rateCard) as CreateRateCardDTO,
    );
    if (res?.id) {
      reset(res);
      navigate(
        generatePath(PageRoutes.platformAnalysisRateCard, {
          rateCardId: res?.id,
        }),
        {
          replace: true,
        },
      );
    }
  };

  const handleOnUpdateRateCard = async (rateCard: RateCardDTO) => {
    const dataSubmit = {
      ...convertDataToSubmit(rateCard),
      isModifiedRateCardData: isDirtyRateCardData,
    } as UpdateRateCardDTO;

    const res = await updateRateCard(dataSubmit);
    if (res) {
      reset(res);
    }
  };

  const handleOnUpdateRateCardStatus = async (status: RateCardStatus) => {
    if (!rateCardId) return;
    setIsChangingStatus(status);
    const res = await updateRateCardStatus(rateCardId, status);
    setIsChangingStatus(null);
    if (res) {
      reset({ ...getValues(), status: res || status });
    }
  };

  const handleOnSelectFeeSchedule = async (
    feeScheduleId?: string,
    isSubProduct?: boolean,
  ) => {
    if (!feeScheduleId) return;

    if (isDirtyRateCardData || (!isDirtyRateCardData && !isNew)) {
      setShowOverrideDialog({
        feeScheduleId: feeScheduleId,
        isSubProduct: isSubProduct,
      });
      return;
    }
    handleOnOverrideFeeSchedule(feeScheduleId, isSubProduct);
  };

  const handleOnOverrideFeeSchedule = async (
    feeScheduleId: string,
    isSubProduct?: boolean,
  ) => {
    let response;

    setIsOverriding(true);
    if (isSubProduct) {
      response = await loadSubProduct(productId, feeScheduleId);
    } else {
      response = await loadRateCard(feeScheduleId);
    }
    setIsOverriding(false);
    setShowOverrideDialog(undefined);

    if (response) {
      const newRateCate = buildRateCate(response, getValues(), isSubProduct);
      setValue("standardFeeSections", newRateCate?.standardFeeSections, {
        shouldDirty: true,
      });
      setValue("otherFeeSections", newRateCate?.otherFeeSections, {
        shouldDirty: true,
      });
    }
  };
  const isSharedWithLinkedPractices = watch("isSharedWithLinkedPractices");
  const isDisabledSharedFirms =
    isSubmitting || !isOwner || (isOwner && isSharedWithLinkedPractices);
  useEffect(() => {
    if (user?.userMetadata?.supplier_id)
      loadSupplier(user.userMetadata.supplier_id);
    return () => {
      refreshSupplier();
    };
    // eslint-disable-next-line
  }, [user?.userMetadata?.supplier_id]);

  useEffect(() => {
    loadRateCardDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, rateCardId]);

  useEffect(() => {
    if (!productId) return;
    loadSubProducts();
    loadFeeSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    handleRateCardNameChecking();
    // eslint-disable-next-line
  }, [sharedWithLinkedPractices, productId]);

  if (rateCardUI.isLoading) return <AnalysisRateCardSkeleton />;

  return (
    <SHContainer sx={{ px: { xs: "16px", lg: 0 } }}>
      <FormProvider {...formControls}>
        <form onSubmit={handleSubmit(handleSubmitRateCard)}>
          <SHStack sx={{ paddingY: 3 }}>
            <TopBar
              navigate={{ to: `${PageRoutes.platformAnalysis}/rate-card` }}
              title={
                <>
                  {/* Controlled but display only */}
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <SHTypography
                        style={{ marginLeft: 8, lineHeight: "120%" }}
                        variant="h1"
                      >
                        {generateRateCardFormTitle(field.value)}
                      </SHTypography>
                    )}
                  />
                  <SHTypography
                    style={{
                      marginLeft: 8,
                      color: palette.secondary.main,
                      paddingTop: 0,
                      lineHeight: "120%",
                    }}
                    variant="h2"
                  >
                    {`by ${isNew ? user?.name : rateCard?.ownerName}`}
                  </SHTypography>
                </>
              }
              customBadge={
                !isNew && (
                  <StatusBadge status={getValues("status") as RateCardStatus} />
                )
              }
              tools={
                <>
                  <SHButton
                    variant="outlined"
                    size="extraMedium"
                    disabled={isSubmitting}
                    onClick={() =>
                      navigate(`${PageRoutes.platformAnalysis}/rate-card`)
                    }
                  >
                    Cancel
                  </SHButton>

                  {!isNew && currentStatus !== RateCardStatus.Archived && (
                    <SHButton
                      variant="outlined"
                      size="extraMedium"
                      isLoading={isChangingStatus === RateCardStatus.Archived}
                      disabled={!isValid || isSubmitting}
                      onClick={() =>
                        handleOnUpdateRateCardStatus(RateCardStatus.Archived)
                      }
                    >
                      Archive
                    </SHButton>
                  )}
                  {!isNew && currentStatus === RateCardStatus.Archived && (
                    <SHButton
                      variant="outlined"
                      size="extraMedium"
                      isLoading={isChangingStatus === RateCardStatus.Active}
                      disabled={!isValid || isSubmitting}
                      onClick={() =>
                        handleOnUpdateRateCardStatus(RateCardStatus.Active)
                      }
                    >
                      Unarchive
                    </SHButton>
                  )}

                  <SHButton
                    variant="contained"
                    size="extraMedium"
                    type="submit"
                    disabled={
                      !isValid || isSubmitting || !isDirty || !isValidName
                    }
                    isLoading={isSubmitting}
                  >
                    Save
                  </SHButton>
                </>
              }
            />
            <FieldGroup
              title="Rate card settings"
              maxWidth="655px"
              subTitle={
                <SHStack
                  sx={{
                    ">p": {
                      fontSize: "13px",
                      fontWeight: 400,
                    },
                    rowGap: "20px",
                  }}
                >
                  <SHTypography>
                    You can use rate cards to show alternative fee arrangements
                    during your platform fee analysis. Rate cards must be
                    selected during the analysis setup stage to be made visible
                    in the fee analysis.
                  </SHTypography>
                  <SHTypography>
                    For non-licensees, any rate cards you create are private to
                    your firm and will not be visible to advice firms or other
                    platform providers. Licensees have the option to share rate
                    cards with linked advice firms. Users in these firms can
                    include shared rate cards in their analyses, but not edit
                    them.
                  </SHTypography>

                  {!isNew &&
                    rateCard?.lastModifiedDate &&
                    rateCard?.createdDate && (
                      <SHTypography
                        style={{
                          fontWeight: 400,
                        }}
                      >
                        Last edit: {rateCard?.lastUpdatedUserName} on
                        {format(
                          new Date(rateCard?.lastModifiedDate),
                          ` ${DateTimeFormat}`,
                        )}
                        <span
                          style={{
                            padding: "0 15px",
                            color: palette.secondary[100],
                          }}
                        >
                          |
                        </span>
                        Created:
                        {format(
                          new Date(rateCard?.createdDate),
                          ` ${DateTimeFormat}`,
                        )}
                      </SHTypography>
                    )}
                </SHStack>
              }
            />
            <SHDivider />
            <FieldGroup
              title="Rate card details"
              subTitle="Provide details to help identify this rate card in the future"
            >
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <SHTextField
                    {...field}
                    hasCounter
                    maxLength={50}
                    label="Name of rate card"
                    required
                    disabled={isSubmitting}
                    error={!!errors.name}
                    sx={{
                      width: { xs: "100%", md: 520, marginBottom: "30px" },
                    }}
                    helperText={
                      errors?.name?.message ??
                      (verifyRateCardName === "error"
                        ? verifyRateCardNameErrorMessage
                        : null)
                    }
                    onChange={(event) => {
                      field.onChange(event.target.value);
                      handleRateCardNameChecking();
                    }}
                    InputProps={{
                      sx: {
                        "&>input": {
                          pr: "32px !important",
                        },
                      },
                      endAdornment: (
                        <SHStack
                          sx={{
                            position: "absolute",
                            right: "8px",
                          }}
                        >
                          {renderVerifyRateCardName()}
                        </SHStack>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <SHRichTextEditor
                    maxLength={3000}
                    label="Notes"
                    placeHolder="Enter notes for this rate card"
                    sx={{ width: { xs: "100%", md: 520 } }}
                    readOnly={isSubmitting}
                    {...field}
                  />
                )}
              />
            </FieldGroup>
            <SHDivider />
            <FieldGroup
              title="Set availability"
              subTitle="Specify if this rate card is available for selection in the platform fee analysis. This setting only impacts your account"
            >
              <Controller
                name="isAvailableForAnalysis"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SHBooleanField
                    value={value}
                    onChange={onChange}
                    sx={{ width: { xs: "100%", md: 520 } }}
                    isSwitch
                    isShowClearButton={false}
                    label="Do you want this rate card to be available for inclusion in your fee analysis?"
                    yesLabel="Available"
                    noLabel="Excluded"
                    width="auto"
                    disabled={isSubmitting}
                  />
                )}
              />
            </FieldGroup>
            <SHDivider />
            {supplierData?.isLicensee && !isSupplierBdmSale && !isAdviserGroup && (
              <>
                <FieldGroup
                  title="Share rate card with all linked practices"
                  subTitle="This setting is exclusive to licensees. Share this rate card with all practices linked to your firm"
                >
                  <Controller
                    name="isSharedWithLinkedPractices"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <SHBooleanField
                        sx={{ width: { xs: "100%", md: 520 } }}
                        value={value}
                        isSwitch
                        isShowClearButton={false}
                        label="Do you want to share this rate card with all practices that are linked to your firm?"
                        yesLabel="Shared"
                        noLabel="Not shared"
                        width="auto"
                        disabled={isSubmitting}
                        onChange={(_, value?: boolean | null) => {
                          onChange(value);
                          if (value) {
                            setValue("isShared", true, { shouldDirty: true });
                            setValue("isAvailableForAnalysis", true, {
                              shouldDirty: true,
                            });
                          }
                        }}
                      />
                    )}
                  />
                </FieldGroup>
                <SHDivider />
              </>
            )}
            {!isAdviserGroup && (
              <FieldGroup
                title="Set sharing within your firm"
                subTitle="Choose whether to share this rate card with others in your team. Only the Owner of the rate card can change this setting. Rate cards shared with linked practices must also be shared with your team"
              >
                <Controller
                  name="isShared"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SHBooleanField
                      sx={{ width: { xs: "100%", md: 520 } }}
                      value={value}
                      onChange={onChange}
                      isSwitch
                      isShowClearButton={false}
                      label={
                        shareLabelForSaleUser
                          ? "Do you want to share this rate card with other adviser facing users in your team?"
                          : "Do you want to share this rate card with other non-adviser facing users in your team?"
                      }
                      yesLabel="Shared"
                      noLabel="Private"
                      width="auto"
                      disabled={isDisabledSharedFirms}
                    />
                  )}
                />
              </FieldGroup>
            )}
            <SHDivider />

            <FieldGroup
              title="Link rate card to a platform"
              subTitle="Rate cards must be assigned to a platform"
              required
            >
              <SHStack
                spacing={3}
                direction="column"
                sx={{ width: { xs: "100%", md: 520 } }}
              >
                <Controller
                  name="productId"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SelectProducts
                      value={value}
                      apiPath={APIExtRoutes.platformAnalysisPublishedProducts}
                      disabled={isSubmitting}
                      multiple={false}
                      maxLength={1}
                      textFieldProps={{
                        label: "Select one from below",
                        placeholder: "Search for platform",
                        required: true,
                        error: !!errors?.productId,
                        helperText: errors?.productId?.message,
                      }}
                      onChange={(value) => {
                        onChange(value);
                        setSelectedFeeSchedule(undefined);
                        setSelectedSubProduct(null);
                        setValue("subProductId", null);
                      }}
                    />
                  )}
                />
              </SHStack>
            </FieldGroup>
            <SHDivider />

            {productId && (
              <>
                <FieldGroup
                  title="Link rate card to a product"
                  subTitle="Rate cards must be assigned to a product"
                  required
                >
                  <SHStack
                    spacing={3}
                    direction="column"
                    sx={{ width: { xs: "100%", md: 520 } }}
                  >
                    <Controller
                      name="subProductId"
                      control={control}
                      render={({ field: { onChange } }) => (
                        <SHAutocomplete
                          disableCloseOnSelect
                          value={selectedSubProduct ?? null}
                          disabled={isSubmitting}
                          options={subProducts ?? []}
                          textFieldProps={{
                            InputLabelProps: { shrink: true },
                            placeholder: selectedSubProduct
                              ? undefined
                              : "Search for a product",
                            label: "Select one from below",
                            required: true,
                            error: !!errors?.subProductId,
                            helperText: errors?.subProductId?.message,
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                          }
                          getOptionLabel={(option: ExistingFeeScheduleDTO) =>
                            option?.name ?? ""
                          }
                          onChange={(
                            _,
                            newValue: ExistingFeeScheduleDTO | null,
                          ) => {
                            onChange(newValue?.id);
                            setSelectedSubProduct(newValue);
                          }}
                          renderOption={(props, option, { selected }) => {
                            return (
                              <li
                                {...props}
                                key={option.id}
                                style={{ padding: "1px 10px" }}
                              >
                                <SHStack
                                  alignItems={"center"}
                                  direction="row"
                                  spacing={1}
                                >
                                  <SHRadio checked={selected} />
                                  <SHTypography
                                    variant="body3"
                                    fontWeight={500}
                                  >
                                    {option.name}
                                  </SHTypography>
                                </SHStack>
                              </li>
                            );
                          }}
                        />
                      )}
                    />
                  </SHStack>
                </FieldGroup>
                <SHDivider />

                <FieldGroup
                  title="Load an existing fee schedule"
                  subTitle="You can either build your rate card from scratch, or you can load the fee schedule of an existing product or a rate card that is linked to the platform you chose"
                >
                  <SHAutocomplete
                    disableCloseOnSelect
                    value={selectedFeeSchedule ?? null}
                    disabled={isSubmitting}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    getOptionLabel={(option: ExistingFeeScheduleDTO) =>
                      option?.name ?? ""
                    }
                    textFieldProps={{
                      InputLabelProps: { shrink: true },
                      placeholder: selectedFeeSchedule
                        ? undefined
                        : "Search for a product or rate card",
                      label: "Select one from below",
                    }}
                    onChange={(_, newValue) => {
                      setSelectedFeeSchedule(
                        newValue as ExistingFeeScheduleDTO,
                      );
                      handleOnSelectFeeSchedule(
                        newValue?.id,
                        newValue?.isSubProduct,
                      );
                    }}
                    options={feeSchedules ?? []}
                    renderOption={(props, option, { selected }) => {
                      return (
                        <li
                          {...props}
                          key={option.id}
                          style={{ padding: "1px 10px" }}
                        >
                          <SHStack
                            alignItems={"center"}
                            direction="row"
                            spacing={1}
                          >
                            <SHRadio checked={selected} />
                            <SHTypography variant="body3" fontWeight={500}>
                              {option.name}
                            </SHTypography>
                          </SHStack>
                        </li>
                      );
                    }}
                    sx={{ width: { xs: "100%", md: 520 } }}
                  />
                </FieldGroup>
                <SHDivider />
              </>
            )}

            <FieldGroup
              title="Build your rate card"
              subTitle={
                <SHStack
                  sx={{
                    ">p": {
                      fontSize: "13px",
                      fontWeight: 400,
                      textAlign: "justify",
                    },
                    rowGap: "20px",
                  }}
                >
                  <SHTypography>
                    Create your rate card here. The more comprehensive the
                    information you enter, the better the fee calculator will
                    function - hence why you may wish to load an existing fee
                    schedule (see the step above).
                  </SHTypography>
                  <SHTypography>
                    Any fields you leave blank will take the default values
                    assigned to our calculator which is often, but not always,
                    zero.
                  </SHTypography>
                  <SHTypography>
                    Careful: If you change the linked platform, product or load
                    from a fee schedule, it can cause you to lose any data
                    already entered into this table
                  </SHTypography>
                </SHStack>
              }
            >
              <SHStack
                sx={{ rowGap: "25px", width: { md: "700px", xs: "100%" } }}
              >
                {/* Standard Fees Table */}
                {renderStandardSections()}
                {/* Other Fees Table */}
                {!isNil(watch("otherFeeSections")) && (
                  <>
                    <SHTypography
                      variant={"subtitle2"}
                      textAlign="left"
                      lineHeight={"160%"}
                    >
                      Other product fees and information
                    </SHTypography>
                    {renderOtherSections()}
                  </>
                )}
              </SHStack>
            </FieldGroup>
          </SHStack>
          <UnsavedDialog isDirty={isDirty && !isSubmitting} />
          {showOverrideDialog && (
            <AnalysisOverwriteFeeScheduleDialog
              onClose={() => setShowOverrideDialog(undefined)}
              onSubmit={() =>
                handleOnOverrideFeeSchedule(
                  showOverrideDialog?.feeScheduleId,
                  showOverrideDialog?.isSubProduct,
                )
              }
              isSubmitting={isOverriding}
            />
          )}
        </form>
      </FormProvider>
    </SHContainer>
  );
};
